import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import { AxiosError } from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import PageHeader from '../../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../../sharedModules/layout/LoadingWrapper';
import ErrorMessage from '../../../../sharedModules/layout/ErrorMessage';
import { SnackBarContext } from '../../../../sharedModules/form/contexts/SnackBarContext';
import {
  CollectionsCorporateLocation,
  getCollectionsSelectedLocation,
} from '../../../navigation/components/CollectionsLocationToggle';
import { useStatementConfig, useUpdateStatementConfig } from '../hooks/useStatementConfig';
import {
  BLANK_STATEMENT_TEMPLATE,
  MONTH_DAYS,
  STATEMENT_FREQUENCY,
  STATEMENT_FREQUENCY_LABEL,
  STATEMENT_TEMPLATE_VARIABLES,
  StatementConfig,
  StatementEmailTemplate,
} from '../model';

const QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
};

const apiErrorMessage = (err: unknown, fallback: string): string => {
  const axiosErr = err as AxiosError<{ error?: string; errors?: string[] }>;
  const body = axiosErr.response?.data;
  if (Array.isArray(body?.errors) && body.errors.length > 0) return body.errors.join(' ');
  if (body?.error) return body.error;
  return fallback;
};

const normalizeTemplate = (raw: StatementEmailTemplate): StatementEmailTemplate => ({
  ...raw,
  frequency: STATEMENT_FREQUENCY,
  schedule_day: null,
  schedule_anchor_date: null,
  schedule_day_of_month: raw.schedule_day_of_month ?? 1,
});

const normalizeConfig = (raw: StatementConfig): StatementConfig => ({
  ...raw,
  sending_enabled: raw.sending_enabled ?? false,
  attach_invoices: raw.attach_invoices ?? true,
  email_template: raw.email_template ? normalizeTemplate(raw.email_template) : { ...BLANK_STATEMENT_TEMPLATE },
});

const buildSavePayload = (config: StatementConfig) => ({
  config: {
    sender_display_name: config.sender_display_name,
    reply_to_email: config.reply_to_email,
    sending_enabled: config.sending_enabled,
    attach_invoices: config.attach_invoices,
    email_template_attributes: config.email_template ? normalizeTemplate(config.email_template) : null,
  },
});

interface LocationTabProps {
  location: CollectionsCorporateLocation;
  enabled: boolean;
}

const LocationTab: React.FC<LocationTabProps> = ({ location: locationKey, enabled }) => {
  const configUrl = `/collections/statement_configs/${locationKey}`;
  const { data, isLoading, isError } = useStatementConfig(locationKey, enabled);
  const { mutateAsync, isPending } = useUpdateStatementConfig();
  const queryClient = useQueryClient();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [config, setConfig] = useState<StatementConfig | null>(null);
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);

  useEffect(() => {
    setConfig(null);
  }, [locationKey]);

  useEffect(() => {
    const result = data?.data;
    if (result) setConfig(normalizeConfig(result));
  }, [data?.data, locationKey]);

  const persistConfig = useCallback(
    async (nextConfig: StatementConfig) => {
      const response = await mutateAsync({ url: configUrl, data: buildSavePayload(nextConfig) });
      const saved = response.data ? normalizeConfig(response.data) : nextConfig;
      setConfig(saved);
      await queryClient.invalidateQueries({ queryKey: [configUrl] });
      return saved;
    },
    [configUrl, mutateAsync, queryClient],
  );

  if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;
  if (isError) return <ErrorMessage message="Failed to load configuration." />;
  if (!config || !config.email_template) return null;

  const template = config.email_template;

  const updateField = (field: keyof StatementConfig, value: unknown) =>
    setConfig((prev) => (prev ? { ...prev, [field]: value } : prev));

  const updateTemplate = (field: keyof StatementEmailTemplate, value: string | number | null) =>
    setConfig((prev) =>
      prev && prev.email_template ? { ...prev, email_template: { ...prev.email_template, [field]: value } } : prev,
    );

  const handleCopyVariable = async (variable: string) => {
    try {
      await navigator.clipboard.writeText(variable);
      showSuccessSnackBar?.(`Copied ${variable}`);
    } catch {
      showErrorSnackBar?.('Failed to copy to clipboard.');
    }
  };

  const handleSave = async () => {
    try {
      await persistConfig(config);
      showSuccessSnackBar?.('Configuration saved.');
    } catch (err) {
      showErrorSnackBar?.(apiErrorMessage(err, 'Failed to save configuration.'));
    }
  };

  const persistSendingEnabled = async (next: boolean) => {
    const previous = config.sending_enabled;
    const nextConfig = { ...config, sending_enabled: next };
    setConfig(nextConfig);
    try {
      await persistConfig(nextConfig);
      showSuccessSnackBar?.(next ? 'Live statement sending enabled.' : 'Live statement sending disabled.');
    } catch (err) {
      setConfig({ ...config, sending_enabled: previous });
      showErrorSnackBar?.(apiErrorMessage(err, 'Failed to update statement sending.'));
    }
  };

  const handleSendingToggle = (checked: boolean) => {
    if (checked) {
      setConfirmEnableOpen(true); // turning ON is high-stakes — confirm first
    } else {
      void persistSendingEnabled(false); // turning OFF is always safe — apply immediately
    }
  };

  const persistAttachInvoices = async (next: boolean) => {
    const previous = config.attach_invoices;
    const nextConfig = { ...config, attach_invoices: next };
    setConfig(nextConfig);
    try {
      await persistConfig(nextConfig);
      showSuccessSnackBar?.(next ? 'Invoice PDF attachments enabled.' : 'Invoice PDF attachments disabled.');
    } catch (err) {
      setConfig({ ...config, attach_invoices: previous });
      showErrorSnackBar?.(apiErrorMessage(err, 'Failed to update invoice attachments.'));
    }
  };

  const invoiceTableMarkerCount = (template.body.match(/\{\{invoice_table\}\}/g) || []).length;

  return (
    <LoadingWrapper isLoading={isPending} message="Saving...">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Grid container spacing={2}>
          {/* Email Sending (live mode) — independent of collections */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Statement Email Sending
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {!config.sending_enabled && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Statement email sending is OFF for {locationKey}. No scheduled or manual statement emails will be
                  delivered until this is turned on.
                </Alert>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={config.sending_enabled}
                    onChange={(e) => handleSendingToggle(e.target.checked)}
                    color="primary"
                    disabled={isPending}
                  />
                }
                label={
                  <Box>
                    <Typography component="span">Live statement sending for {locationKey}</Typography>
                    <Typography variant="body2" color="textSecondary" display="block">
                      When on, statement emails are delivered to clients. When off, nothing is sent.
                    </Typography>
                  </Box>
                }
              />
            </Paper>
          </Grid>

          {/* Invoice Attachments — per-location default */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Invoice Attachments
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {!config.attach_invoices && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Invoice PDF attachments are OFF for {locationKey}. No statement emails for this location will include
                  invoice PDFs until this is turned on.
                </Alert>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={config.attach_invoices}
                    onChange={(e) => void persistAttachInvoices(e.target.checked)}
                    color="primary"
                    disabled={isPending}
                  />
                }
                label={
                  <Box>
                    <Typography component="span">Attach invoice PDFs to statement emails for {locationKey}</Typography>
                    <Typography variant="body2" color="textSecondary" display="block">
                      Default for all clients in this location. When off, no client in {locationKey} attaches invoices,
                      regardless of their per-client setting. Per-client overrides live on the Statement List.
                    </Typography>
                  </Box>
                }
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Sender Settings */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sender Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sender Display Name"
                fullWidth
                value={config.sender_display_name}
                onChange={(e) => updateField('sender_display_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Reply-To Email"
                type="email"
                fullWidth
                value={config.reply_to_email}
                onChange={(e) => updateField('reply_to_email', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Email Template (single) */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Statement Email Template
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Frequency"
                fullWidth
                value={STATEMENT_FREQUENCY_LABEL}
                disabled
                helperText="Statement emails are sent monthly."
              />
            </Grid>

            <Grid item xs={6} sm={4}>
              <TextField
                select
                label="Day of Month"
                fullWidth
                value={template.schedule_day_of_month ?? 1}
                onChange={(e) => updateTemplate('schedule_day_of_month', parseInt(e.target.value, 10))}
                helperText="Months with fewer days will send on the last day."
              >
                {MONTH_DAYS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} sm={4}>
              <TextField
                label="Schedule Time (HH:MM)"
                fullWidth
                value={template.schedule_time}
                onChange={(e) => updateTemplate('schedule_time', e.target.value)}
                placeholder="09:00"
              />
            </Grid>
          </Grid>

          <TextField
            label="Email Subject"
            fullWidth
            value={template.subject}
            onChange={(e) => updateTemplate('subject', e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Template Variables — click to copy, then paste into the subject or body
            </Typography>
            {STATEMENT_TEMPLATE_VARIABLES.map(({ variable, description }) => (
              <Tooltip key={variable} title={description}>
                <Chip
                  icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  label={variable}
                  onClick={() => handleCopyVariable(variable)}
                  clickable
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1, mb: 0.5 }}
                />
              </Tooltip>
            ))}
          </Box>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            Email Body
          </Typography>
          <ReactQuill
            placeholder="Enter email body here..."
            theme="snow"
            value={template.body}
            onChange={(value) => updateTemplate('body', value)}
            modules={QUILL_MODULES}
            style={{ height: 200, marginBottom: 48 }}
          />

          {invoiceTableMarkerCount > 1 && (
            <FormHelperText error sx={{ mb: 1 }}>
              Only the first {'{{invoice_table}}'} will render the invoice table. Subsequent markers will appear as
              literal text in the email.
            </FormHelperText>
          )}

          <Box sx={{ mt: 2, p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Invoice Table Preview — rendered in the email wherever{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                {'{{invoice_table}}'}
              </Box>{' '}
              appears in the body above. The list of invoices is generated at send time from the customer&apos;s open
              invoices.
            </Typography>
            <Table size="small" sx={{ bgcolor: '#ffffff' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#1e3a5f' }}>
                  {['Date', 'Age', 'Invoice', 'PO', 'Reference'].map((label) => (
                    <TableCell key={label} sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {label}
                    </TableCell>
                  ))}
                  {['Amount', 'Balance'].map((label) => (
                    <TableCell key={label} sx={{ color: '#fff', fontWeight: 'bold' }} align="right">
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>02/10/2026</TableCell>
                  <TableCell>45</TableCell>
                  <TableCell>159015</TableCell>
                  <TableCell>PO-778</TableCell>
                  <TableCell>Maintenance</TableCell>
                  <TableCell align="right">$500.00</TableCell>
                  <TableCell align="right">$340.00</TableCell>
                </TableRow>
                <TableRow sx={{ borderTop: '2px solid #1e3a5f' }}>
                  <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>
                    Total Balance
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    $340.00
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <Box>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save Configuration
          </Button>
        </Box>
      </Box>

      <Dialog open={confirmEnableOpen} onClose={() => setConfirmEnableOpen(false)}>
        <DialogTitle>Enable live statement sending?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will send real statement emails to customers in {locationKey}. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEnableOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setConfirmEnableOpen(false);
              void persistSendingEnabled(true);
            }}
          >
            Enable
          </Button>
        </DialogActions>
      </Dialog>
    </LoadingWrapper>
  );
};

const StatementConfigPage: React.FC = () => {
  const [location] = useState(() => getCollectionsSelectedLocation());

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Statements — Configuration" titleIcon={<SettingsIcon />} />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <LocationTab location={location} enabled />
      </Grid>
    </Grid>
  );
};

export default StatementConfigPage;
